// src/pages/Accounting/Reconcile/Session/SessionTable.tsx

import type React from "react";
import { Check } from "lucide-react";
import { PALETTE } from "../constants";
import type { Currency } from "../types";
import { currencySymbol, formatIsoToUserDate } from "../utils";
import type { ColumnKey, Density, SessionTransactionRich } from "./sessionTypes";
import { TRANSACTION_KIND_LABELS } from "./sessionTypes";
import { SessionRowEdit } from "./SessionRowEdit";

interface SessionTableProps {
  transactions: SessionTransactionRich[];
  clearedIds: Set<string>;
  onToggleCleared: (id: string) => void;
  onRowClick: (id: string) => void;
  editingId: string | null;
  onCancelEdit: () => void;
  onSaveEdit: (updated: SessionTransactionRich) => void;
  onOpenFullEditor: (id: string) => void;
  visibleColumns: ColumnKey[];
  density: Density;
  currency: Currency;
}

const DENSITY_PADDING: Record<Density, string> = {
  regular: "14px 0",
  compact: "10px 0",
  ultra: "7px 0",
};

interface ColumnDef {
  key: string;
  label: string;
  width: string;
  align?: "right";
}

function buildColumns(visible: Set<ColumnKey>): ColumnDef[] {
  const cols: ColumnDef[] = [
    { key: "check", label: "", width: "44px" },
    { key: "date", label: "Date", width: "90px" },
  ];
  if (visible.has("clearedDate")) cols.push({ key: "clearedDate", label: "Cleared", width: "95px" });
  if (visible.has("type")) cols.push({ key: "type", label: "Type", width: "130px" });
  if (visible.has("refNo")) cols.push({ key: "refNo", label: "Ref no.", width: "100px" });
  if (visible.has("account")) cols.push({ key: "account", label: "Account", width: "180px" });
  if (visible.has("payee")) cols.push({ key: "payee", label: "Payee", width: "170px" });
  if (visible.has("memo")) cols.push({ key: "memo", label: "Memo", width: "1fr" });
  cols.push({ key: "payment", label: "Payment", width: "110px", align: "right" });
  cols.push({ key: "deposit", label: "Deposit", width: "110px", align: "right" });
  return cols;
}

export function SessionTable({
  transactions,
  clearedIds,
  onToggleCleared,
  onRowClick,
  editingId,
  onCancelEdit,
  onSaveEdit,
  onOpenFullEditor,
  visibleColumns,
  density,
  currency,
}: SessionTableProps) {
  const sym = currencySymbol(currency);
  const visibleSet = new Set(visibleColumns);
  const columns = buildColumns(visibleSet);
  const gridTemplate = columns.map((c) => c.width).join(" ");

  return (
    <div style={wrapStyle}>
      <div style={{ ...headStyle, gridTemplateColumns: gridTemplate }}>
        {columns.map((c) => (
          <div key={c.key} style={c.align === "right" ? headCellRightStyle : undefined}>
            {c.label}
          </div>
        ))}
      </div>

      {transactions.map((tx) => {
        const checked = clearedIds.has(tx.id);
        const isFlagged = tx.matchStatus === "unmatched" || tx.matchStatus === "needs-review";
        const isEditing = editingId === tx.id;
        const rowBase: React.CSSProperties = {
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap: 12,
          alignItems: "center",
          padding: DENSITY_PADDING[density],
          borderBottom: `1px solid ${PALETTE.lineSoft}`,
          boxShadow: isFlagged ? `inset 3px 0 0 ${PALETTE.amber}` : "none",
          fontSize: 13,
          cursor: "pointer",
        };
        const isPayment = tx.amount < 0;
        const isDeposit = tx.amount > 0;

        return (
          <div key={tx.id}>
            <div style={rowBase} onClick={() => onRowClick(tx.id)}>
              {columns.map((c) => {
                if (c.key === "check") {
                  return (
                    <div
                      key="check"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleCleared(tx.id);
                      }}
                    >
                      <span
                        style={{
                          ...checkRoundStyle,
                          ...(checked ? checkCheckedStyle : isFlagged ? checkAlertStyle : {}),
                        }}
                      >
                        {checked && <Check size={12} strokeWidth={3} color="#fff" />}
                      </span>
                    </div>
                  );
                }
                if (c.key === "date") return <div key="date" style={cellMutedStyle}>{formatIsoToUserDate(tx.date)}</div>;
                if (c.key === "clearedDate") return <div key="clearedDate" style={cellMutedStyle}>{tx.clearedDate ? formatIsoToUserDate(tx.clearedDate) : ""}</div>;
                if (c.key === "type") return <div key="type" style={cellClickableStyle}>{TRANSACTION_KIND_LABELS[tx.kind]}</div>;
                if (c.key === "refNo") return <div key="refNo" style={{ ...cellMutedStyle, fontVariantNumeric: "tabular-nums" }}>{tx.refNo ?? ""}</div>;
                if (c.key === "account") return <div key="account" style={cellClickableTruncStyle}>{tx.account}</div>;
                if (c.key === "payee") return <div key="payee" style={cellClickableTruncStyle}>{tx.payee}</div>;
                if (c.key === "memo") return <div key="memo" style={cellMemoStyle}>{tx.memo ?? ""}</div>;
                if (c.key === "payment") return <div key="payment" style={{ ...cellAmountStyle, color: PALETTE.ink }}>{isPayment ? `${sym}${Math.abs(tx.amount).toFixed(2)}` : ""}</div>;
                if (c.key === "deposit") return <div key="deposit" style={{ ...cellAmountStyle, color: PALETTE.tealDark }}>{isDeposit ? `${sym}${tx.amount.toFixed(2)}` : ""}</div>;
                return <div key={c.key} />;
              })}
            </div>
            {isEditing && (
              <div onClick={(e) => e.stopPropagation()}>
                <SessionRowEdit
                  transaction={tx}
                  onCancel={onCancelEdit}
                  onSave={onSaveEdit}
                  onOpenFullEditor={onOpenFullEditor}
                />
              </div>
            )}
          </div>
        );
      })}

      {transactions.length === 0 && (
        <div style={emptyStyle}>No transactions match the current filter.</div>
      )}
    </div>
  );
}

const wrapStyle: React.CSSProperties = { background: "#fff", padding: "0 32px 60px" };
const headStyle: React.CSSProperties = { display: "grid", gap: 12, alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${PALETTE.line}`, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.ink3, fontWeight: 700 };
const headCellRightStyle: React.CSSProperties = { textAlign: "right" };
const cellMutedStyle: React.CSSProperties = { color: PALETTE.ink3, fontWeight: 500 };
const cellClickableStyle: React.CSSProperties = { color: PALETTE.ink, fontWeight: 500, cursor: "pointer" };
const cellClickableTruncStyle: React.CSSProperties = { ...cellClickableStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const cellMemoStyle: React.CSSProperties = { color: PALETTE.ink3, fontWeight: 500, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const cellAmountStyle: React.CSSProperties = { fontWeight: 600, fontVariantNumeric: "tabular-nums", textAlign: "right" };
const checkRoundStyle: React.CSSProperties = { width: 22, height: 22, border: `1.5px solid ${PALETTE.line}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "#fff" };
const checkCheckedStyle: React.CSSProperties = { background: PALETTE.tealDark, borderColor: PALETTE.tealDark };
const checkAlertStyle: React.CSSProperties = { borderColor: PALETTE.amber, background: "#FFFBEB" };
const emptyStyle: React.CSSProperties = { padding: "40px 0", textAlign: "center", color: PALETTE.ink3, fontSize: 13, fontWeight: 500 };